import React from 'react';
import { TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

/**
 * Badge de Recurribilidad / Probabilidad de Exito
 *
 * Muestra la probabilidad de exito de un recurso con colores semanticos:
 * - Verde (>=70%): Alta probabilidad
 * - Amarillo (40-69%): Valorar
 * - Rojo (<40%): Baja probabilidad
 *
 * Props:
 * - probabilidad: number (0-100)
 * - size: 'sm' | 'md' | 'lg' (default: 'md')
 * - showIcon: boolean (default: true)
 * - showLabel: boolean (default: false)
 * - compact: boolean (default: false)
 */
function RecurribilityBadge({
  probabilidad,
  size = 'md',
  showIcon = true,
  showLabel = false,
  compact = false
}) {
  // Determinar categoria y estilos
  const getCategoria = (prob) => {
    if (prob >= 70) {
      return {
        nivel: 'alto',
        label: 'Alta probabilidad',
        labelCorto: 'Alta',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-300',
        iconColor: 'text-green-600',
        icon: TrendingUp,
        recomendacion: 'RECOMENDADO'
      };
    }
    if (prob >= 40) {
      return {
        nivel: 'medio',
        label: 'Valorar caso',
        labelCorto: 'Valorar',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-300',
        iconColor: 'text-yellow-600',
        icon: Minus,
        recomendacion: 'VALORAR'
      };
    }
    return {
      nivel: 'bajo',
      label: 'Baja probabilidad',
      labelCorto: 'Baja',
      bgColor: 'bg-red-100',
      textColor: 'text-red-800',
      borderColor: 'border-red-300',
      iconColor: 'text-red-600',
      icon: TrendingDown,
      recomendacion: 'NO_RECOMENDADO'
    };
  };

  // Tamaños
  const sizes = {
    sm: {
      padding: 'px-2 py-0.5',
      text: 'text-xs',
      icon: 'w-3 h-3',
      font: 'font-medium'
    },
    md: {
      padding: 'px-3 py-1',
      text: 'text-sm',
      icon: 'w-4 h-4',
      font: 'font-semibold'
    },
    lg: {
      padding: 'px-4 py-2',
      text: 'text-base',
      icon: 'w-5 h-5',
      font: 'font-bold'
    }
  };

  const categoria = getCategoria(probabilidad);
  const sizeStyles = sizes[size];
  const Icon = categoria.icon;

  // Modo compacto: solo el porcentaje
  if (compact) {
    return (
      <span className={`
        inline-flex items-center gap-1 rounded-full
        ${categoria.bgColor} ${categoria.textColor}
        ${sizeStyles.padding} ${sizeStyles.text} ${sizeStyles.font}
      `}>
        {showIcon && <Icon className={sizeStyles.icon} />}
        {probabilidad}%
      </span>
    );
  }

  return (
    <div className={`
      inline-flex items-center gap-2 rounded-lg border
      ${categoria.bgColor} ${categoria.borderColor}
      ${sizeStyles.padding}
    `}>
      {showIcon && (
        <Icon className={`${sizeStyles.icon} ${categoria.iconColor}`} />
      )}
      <div className="flex items-center gap-1">
        <span className={`${sizeStyles.font} ${categoria.textColor} ${sizeStyles.text}`}>
          {probabilidad}%
        </span>
        {showLabel && (
          <span className={`${categoria.textColor} ${sizeStyles.text} opacity-75`}>
            - {categoria.labelCorto}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Componente de Recomendacion completo con explicacion
 */
export function RecurribilityCard({ probabilidad, factoresPositivos = [], factoresNegativos = [] }) {
  const getCategoria = (prob) => {
    if (prob >= 70) {
      return {
        nivel: 'alto',
        titulo: 'Alta Probabilidad de Exito',
        descripcion: 'Recomendamos presentar el recurso. Los factores son favorables.',
        color: 'green',
        icon: CheckCircle
      };
    }
    if (prob >= 40) {
      return {
        nivel: 'medio',
        titulo: 'Probabilidad Media',
        descripcion: 'Valorar segun circunstancias particulares del caso.',
        color: 'yellow',
        icon: AlertTriangle
      };
    }
    return {
      nivel: 'bajo',
      titulo: 'Baja Probabilidad',
      descripcion: 'No recomendamos recurrir. Los factores no son favorables.',
      color: 'red',
      icon: XCircle
    };
  };

  const categoria = getCategoria(probabilidad);
  const Icon = categoria.icon;

  const colorClasses = {
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      icon: 'text-green-600',
      title: 'text-green-900',
      bar: 'bg-green-500'
    },
    yellow: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      icon: 'text-yellow-600',
      title: 'text-yellow-900',
      bar: 'bg-yellow-500'
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      icon: 'text-red-600',
      title: 'text-red-900',
      bar: 'bg-red-500'
    }
  };

  const colors = colorClasses[categoria.color];

  return (
    <div className={`rounded-xl border ${colors.bg} ${colors.border} p-6`}>
      {/* Header con probabilidad */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colors.bg}`}>
            <Icon className={`w-6 h-6 ${colors.icon}`} />
          </div>
          <div>
            <h3 className={`font-bold ${colors.title}`}>{categoria.titulo}</h3>
            <p className="text-sm text-gray-600">{categoria.descripcion}</p>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-4xl font-bold ${colors.icon}`}>{probabilidad}%</div>
          <div className="text-xs text-gray-500 uppercase">Prob. Exito</div>
        </div>
      </div>

      {/* Barra de progreso */}
      <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
        <div
          className={`h-3 rounded-full transition-all ${colors.bar}`}
          style={{ width: `${probabilidad}%` }}
        />
      </div>

      {/* Factores */}
      {(factoresPositivos.length > 0 || factoresNegativos.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          {/* Factores positivos */}
          {factoresPositivos.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-green-700 mb-2 flex items-center gap-1">
                <TrendingUp className="w-4 h-4" /> A favor
              </h4>
              <ul className="space-y-1">
                {factoresPositivos.slice(0, 3).map((factor, i) => (
                  <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                    <span className="text-green-500 mt-1">+</span>
                    {typeof factor === 'string' ? factor : factor.factor}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Factores negativos */}
          {factoresNegativos.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
                <TrendingDown className="w-4 h-4" /> En contra
              </h4>
              <ul className="space-y-1">
                {factoresNegativos.slice(0, 3).map((factor, i) => (
                  <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                    <span className="text-red-500 mt-1">-</span>
                    {typeof factor === 'string' ? factor : factor.factor}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RecurribilityBadge;
